import { useCallback } from 'react'
import styled, { css } from 'styled-components'

import translate from '@i18n'
import SvgIcon from '@components/SvgIcon'
import useTracking from '@hooks/useTracking'
import { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import { SportEntitySubscription } from '@hooks/useSportEntitySubscriptions'

import FollowedTeamsList from './FollowedTeamsList'

const Wrapper = styled.div`
  ${({
    theme: {
      colors: { strokeWeak },
      spacing: { spacing8 },
    },
  }) => css`
    position: relative;
    overflow: hidden;
    height: 100%;
    border: 1px solid ${strokeWeak};
    box-sizing: border-box;
    border-radius: ${spacing8};
  `}
`

const FollowMyTeamSettings = styled.div`
  ${({
    theme: {
      colors: { strokeWeak },
      spacing: { spacing8 },
    },
  }) => css`
    height: 100%;
    width: 73px;
    right: 0;
    top: 0;
    box-sizing: border-box;

    border-left: 1px solid ${strokeWeak};
    padding: ${spacing8};

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    position: absolute;
    right: 0;

    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
  `}
`

const FollowMyTeamSettingsText = styled.span`
  ${({
    theme: {
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: bodyCopyXXSmallCSS },
        },
      },
    },
  }) => css`
    ${bodyCopyXXSmallCSS}
  `}
`

interface FollowMyTeamOverviewWithSubscriptionsProps {
  sportEntitySubscriptions: SportEntitySubscription[]
  onSettingsClick: () => void
  onAddTeamClick: () => void
}

const FollowMyTeamOverviewWithSubscriptions = ({
  sportEntitySubscriptions,
  onSettingsClick,
  onAddTeamClick,
}: FollowMyTeamOverviewWithSubscriptionsProps) => {
  const onImpressionEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_impression',
      element: 'fmt_overview',
    }),
    []
  )

  const onItemClickEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'fmt_overview',
      click_action: 'pill',
    }),
    []
  )

  const onSettingsClickEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'fmt_overview',
      click_action: 'adjust',
    }),
    []
  )

  const onAddTeamClickEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'element_click',
      element: 'fmt_overview',
      click_action: 'add',
    }),
    []
  )

  const trackedOnImpression = useTracking(onImpressionEvent)
  const trackedOnItemClick = useTracking(onItemClickEvent)
  const trackedOnSettingsClick = useTracking(onSettingsClickEvent)
  const trackedOnAddTeamClick = useTracking(onAddTeamClickEvent)

  const viewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnImpression,
  })

  const handleItemClick = useCallback(() => {
    trackedOnItemClick()
  }, [trackedOnItemClick])

  const handleSettingsClick = useCallback(() => {
    onSettingsClick()
    trackedOnSettingsClick()
  }, [onSettingsClick, trackedOnSettingsClick])

  const handleAddTeamClick = useCallback(() => {
    onAddTeamClick()
    trackedOnAddTeamClick()
  }, [onAddTeamClick, trackedOnAddTeamClick])

  return (
    <Wrapper ref={viewportRef}>
      <FollowedTeamsList
        items={sportEntitySubscriptions}
        onAddTeamClick={handleAddTeamClick}
        onItemClick={handleItemClick}
      />
      <FollowMyTeamSettings onClick={handleSettingsClick}>
        <SvgIcon iconName="settings" size="24px" />
        <FollowMyTeamSettingsText>
          {translate('followMyTeamOverview.settings')}
        </FollowMyTeamSettingsText>
      </FollowMyTeamSettings>
    </Wrapper>
  )
}

export default FollowMyTeamOverviewWithSubscriptions
